import { Dispatch } from "redux";
import { datadogLogs } from "@datadog/browser-logs";
import { resources } from "@constants/hardcoded-strings";
import { AdvancedWorkflowDetailsConfiguration } from "@idw-types/data/workflow-details-configuration";
import { WorkflowChangeRequest } from "@idw-types/data/workflow-change-request";
import { setAdvancedWorkflowDetailsLoading } from "@redux/actions/workflow-details-actions";
import {
    getWorkflowChangeRequest,
    getWorkflowDetailsAxnAdminRequest,
    getWorkflowDetailsAxnRequest
} from "@requests/service-configuration";
import { logAndDisplayErrorModal } from "@utils/app-alert-util";

const getWorkflowDetailsFromAxn = async (workflowId: string, dispatch: Dispatch, workflowNotFoundCallback: () => void, showError: boolean): Promise<AdvancedWorkflowDetailsConfiguration> => {
    const axnWorkflowDetailsResponse = await getWorkflowDetailsAxnRequest(workflowId);

    if (axnWorkflowDetailsResponse.data) {
        return axnWorkflowDetailsResponse.data;
    } else if (axnWorkflowDetailsResponse.error) {
        const errorCode = JSON.parse(axnWorkflowDetailsResponse.error).code;
        if (!showError && errorCode === 404) {
            workflowNotFoundCallback();
        } else {
            logAndDisplayErrorModal(
                dispatch,
                axnWorkflowDetailsResponse.error,
                resources.requests.getWorkflowDetails.error.title,
                resources.requests.getWorkflowDetails.error.message
            );
        }
    }
    return null;
};

export const getAdvancedWorkflowDetailsReduxRequest = (dispatch: Dispatch) => async (
    workflowId: string,
    workflowNotFoundCallback: () => void,
    showError: boolean
): Promise<{
    advancedWorkflowDetails: AdvancedWorkflowDetailsConfiguration;
    workflowChangeRequests: Array<WorkflowChangeRequest>;
}> => {
    dispatch(setAdvancedWorkflowDetailsLoading(true));
    const workflowChangeRequest = await getWorkflowChangeRequest(workflowId);

    let workflowDetails: AdvancedWorkflowDetailsConfiguration;
    if (workflowChangeRequest.data?.workflowChangeRequests) {
        // check change requests - if one exists, attempt to get data from admin
        if (workflowChangeRequest.data.workflowChangeRequests.length > 0) {
            const axnAdminWorkflowDetailsResponse = await getWorkflowDetailsAxnAdminRequest(workflowId);
            // if admin data does not exist, attempt to get from axn
            if (axnAdminWorkflowDetailsResponse.data) {
                workflowDetails = axnAdminWorkflowDetailsResponse.data;
            } else {
                const errorMessage = `Workflow with ID ${workflowId} has a change request but is not present in AXN Admin`;
                console.error(errorMessage);
                datadogLogs.logger.error(errorMessage);

                workflowDetails = await getWorkflowDetailsFromAxn(workflowId, dispatch, workflowNotFoundCallback, showError);
            }
        } else {
            //if no change requests exist, attempt to get data from axn
            workflowDetails = await getWorkflowDetailsFromAxn(workflowId, dispatch, workflowNotFoundCallback, showError);
        }

    }

    if (workflowDetails) {
        return {
            advancedWorkflowDetails: workflowDetails,
            workflowChangeRequests: workflowChangeRequest.data?.workflowChangeRequests || []
        };
    } else {
        dispatch(setAdvancedWorkflowDetailsLoading(false));
        return null;
    }
};